import * as React from 'react';

import { faXmarkCircle } from '@fortawesome/pro-regular-svg-icons';
import { useIntl } from 'react-intl';

import { useCancelReservation } from 'src/features/reservations/hooks/useCancelReservation.ts';
import { reservationMessages } from 'src/features/reservations/utils/messages.ts';
import { useParking } from 'src/providers/ParkingProvider.tsx';
import SquareIcon from 'src/registry/components/blocks/SquareIcon.tsx';
import { toast } from 'src/registry/hooks/UseToast.ts';
import { Button } from 'src/registry/ui/Button.tsx';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogDescription,
} from 'src/registry/ui/Dialog.tsx';
import { generalErrorMessages, generalMessages } from 'src/utils/messages.ts';

import type { ReservationsVehicleStayList } from 'src/features/reservations/types/reservationType.ts';

interface CancelReservationModalProps extends React.HTMLAttributes<HTMLDivElement> {
    selectedReservation?: ReservationsVehicleStayList;
    refetch: () => void;
}

const CancelReservationModal = ({ selectedReservation, refetch, children }: CancelReservationModalProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const reservationNumber = selectedReservation?.number;

    const { mutate: cancelReservation } = useCancelReservation({
        onSuccess: () => {
            setIsModalOpen(!isModalOpen);
            void refetch();
            toast({
                title: formatMessage(reservationMessages.cancelReservationSuccess),
                description: formatMessage(reservationMessages.cancelReservationSuccessDetails, {
                    reservationNumber,
                }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={() => setIsModalOpen(!isModalOpen)}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={faXmarkCircle} />
                        <div>
                            <DialogTitle>{formatMessage(reservationMessages.cancelReservationTitle)}</DialogTitle>
                            <DialogDescription>{`${formatMessage(reservationMessages.cancelReservationTitle)} #${selectedReservation?.number}`}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        {formatMessage(reservationMessages.confirmCancelReservation, { reservationNumber })}
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        onClick={() => {
                            if (selectedReservation) {
                                void cancelReservation({ parkingId, reservationId: selectedReservation.id });
                            }
                        }}
                        className="flex-1"
                    >
                        {formatMessage(reservationMessages.cancelReservationTitle)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CancelReservationModal;
